import { getAccessProfileQueryKey } from "@packages/api-client/hooks/authorizationController";
import { getMyOrganizationsQueryOptions } from "@packages/api-client/hooks/organizationController";
import {
	getPartnerEnrollmentConfigQueryOptions,
	getSubscriptionQueryOptions,
} from "@packages/api-client/hooks/platformBillingController";
import { isPartnerPlanSlug } from "@packages/shared-types";
import {
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	ArrowLeft,
	Building2,
	CheckCircle,
	CreditCard,
	HandCoins,
	Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { PlatformPlanContent } from "@/components/PlatformPlanContent";
import { RedeemPartnerCodeDialog } from "@/components/RedeemPartnerCodeDialog";
import { SyncSubscriptionButton } from "@/components/SyncSubscriptionButton";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDateFormatter } from "@/hooks/useDateFormatter";

const renewSubscriptionSearchSchema = z.object({
	orgId: z.string(),
});

export const Route = createFileRoute("/_auth/app/_app/org/renew-platform-plan")(
	{
		validateSearch: renewSubscriptionSearchSchema,
		loader: async ({ context }) => {
			// Ensure organizations are loaded so we can derive org name
			context.queryClient.ensureQueryData(getMyOrganizationsQueryOptions());
		},
		component: RouteComponent,
	},
);

function RouteComponent() {
	const { t } = useTranslation();
	const { formatDateOnly } = useDateFormatter();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { orgId } = Route.useSearch();

	// Modal state. `welcomeVariant` picks the copy: a free/partner activation vs
	// a card-free trial start (same modal chrome + auto-redirect).
	const [showWelcomeModal, setShowWelcomeModal] = useState(false);
	const [welcomeVariant, setWelcomeVariant] = useState<"free" | "trial">(
		"free",
	);
	const [countdown, setCountdown] = useState(5);
	const [showRedeemDialog, setShowRedeemDialog] = useState(false);

	// Fetch organizations to get the org name
	const { data: organizations } = useSuspenseQuery(
		getMyOrganizationsQueryOptions(),
	);

	const organization = organizations.find((org) => org.id === orgId);
	const organizationName = organization?.name ?? orgId;

	// Non-suspense reads for the partner-code UX. Both retry:false — a
	// transient failure of the config endpoint degrades to "button hidden"
	// (safe default) rather than throwing to the route error boundary and
	// blanking the whole plan-picker page.
	const { data: partnerConfig } = useQuery({
		...getPartnerEnrollmentConfigQueryOptions(orgId),
		retry: false,
	});
	const { data: subscriptionData } = useQuery({
		...getSubscriptionQueryOptions(orgId),
		retry: false,
	});
	const partnerEnrollmentEnabled = partnerConfig?.enabled ?? false;
	const currentPlanSlug = subscriptionData?.plan?.slug;
	const hasActivePartnerSub =
		subscriptionData?.subscription?.status === "ACTIVE" &&
		typeof currentPlanSlug === "string" &&
		isPartnerPlanSlug(currentPlanSlug);
	const showRedeemButton = partnerEnrollmentEnabled && !hasActivePartnerSub;

	const handleGoToDashboard = useCallback(async () => {
		// Invalidate access profile query to ensure fresh subscription status is fetched
		const [baseKey] = getAccessProfileQueryKey({ organizationId: orgId });
		await queryClient.invalidateQueries({
			queryKey: [baseKey],
		});
		navigate({ to: "/app/org/$orgId", params: { orgId } });
	}, [navigate, orgId, queryClient]);

	// Handle free plan success - show modal
	const handleFreePlanSuccess = useCallback(() => {
		setWelcomeVariant("free");
		setCountdown(5);
		setShowWelcomeModal(true);
	}, []);

	// Handle card-free trial start - show the trial-flavored welcome modal
	const handleTrialSuccess = useCallback(() => {
		setWelcomeVariant("trial");
		setCountdown(5);
		setShowWelcomeModal(true);
	}, []);

	// Auto-redirect countdown
	useEffect(() => {
		if (!showWelcomeModal) return;

		if (countdown <= 0) {
			handleGoToDashboard();
			return;
		}

		const timer = setTimeout(() => {
			setCountdown((prev) => prev - 1);
		}, 1000);

		return () => clearTimeout(timer);
	}, [showWelcomeModal, countdown, handleGoToDashboard]);

	return (
		<div className="h-full overflow-y-auto bg-base-200">
			{/* Welcome Modal */}
			<Dialog open={showWelcomeModal} onOpenChange={setShowWelcomeModal}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader className="text-center">
						<div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
							<CheckCircle className="h-10 w-10 text-success" />
						</div>
						<DialogTitle className="text-xl text-center">
							{welcomeVariant === "trial"
								? t("platformPlan.welcomeModal.trialTitle")
								: t("platformPlan.welcomeModal.title")}
						</DialogTitle>
						<DialogDescription className="text-center">
							{welcomeVariant === "trial"
								? t("platformPlan.welcomeModal.trialDescription")
								: t("platformPlan.welcomeModal.description")}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="flex-col gap-2 sm:flex-col">
						<Button onClick={handleGoToDashboard} className="w-full">
							{t("platformPlan.welcomeModal.goToDashboard")}
						</Button>
						<p className="text-center text-sm text-base-content/70">
							{t("platformPlan.welcomeModal.redirectingIn", {
								seconds: countdown,
							})}
						</p>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Header */}
			<div className="bg-base-100 border-b border-base-300 sticky top-0 z-10">
				<div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
					<div className="flex items-center justify-between gap-2">
						<div className="flex items-center gap-2 sm:gap-4 min-w-0">
							<Link to="/app/select-organization" className="shrink-0">
								<Button variant="ghost" size="sm" className="px-2 sm:px-3">
									<ArrowLeft className="h-4 w-4 sm:mr-2" />
									<span className="hidden sm:inline">{t("common.back")}</span>
								</Button>
							</Link>
							<div className="hidden sm:block h-6 w-px bg-base-300 shrink-0" />
							<div className="flex items-center gap-2 min-w-0">
								<div className="p-2 bg-primary/10 rounded-lg shrink-0">
									<CreditCard className="h-5 w-5 text-primary" />
								</div>
								<div className="min-w-0">
									<h1 className="text-base sm:text-lg font-semibold truncate">
										{t("common.renewSubscription_title")}
									</h1>
									<div className="flex items-center gap-1 text-sm text-base-content/70 min-w-0">
										<Building2 className="h-3 w-3 shrink-0" />
										<span className="truncate">{organizationName}</span>
									</div>
								</div>
							</div>
						</div>
						<div className="flex items-center gap-1 sm:gap-2 shrink-0">
							{showRedeemButton ? (
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											onClick={() => setShowRedeemDialog(true)}
											data-testid="open-partner-redeem"
											aria-label={t("platformPlan.partnerRedeem.openCta")}
										>
											<HandCoins className="h-4 w-4" />
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										{t("platformPlan.partnerRedeem.openCta")}
									</TooltipContent>
								</Tooltip>
							) : null}
							<SyncSubscriptionButton orgId={orgId} />
						</div>
					</div>
				</div>
			</div>

			{/* Redeem partner code dialog (only mounted when the kill-switch is on
			    and the org isn't already on an active partner sub). */}
			{showRedeemButton ? (
				<RedeemPartnerCodeDialog
					open={showRedeemDialog}
					onOpenChange={setShowRedeemDialog}
					organizationId={orgId}
					onRedeemed={handleFreePlanSuccess}
				/>
			) : null}

			{/* Content */}
			<div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
				{/* Existing-partner banner — when the org is already on an active
				    partner subscription, surface it above the plan grid. The grid
				    below still renders so the user can upgrade to a paid plan
				    whenever they want (per §F#3 of the partner plan). */}
				{hasActivePartnerSub && subscriptionData?.subscription ? (
					<div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
						<div className="flex items-start gap-3">
							<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
								<Sparkles className="h-4 w-4 text-primary" />
							</div>
							<div className="text-sm">
								<p className="font-medium text-base-content">
									{t("platformPlan.partnerRedeem.activeBanner.title", {
										planName: subscriptionData.plan?.name ?? "",
										date: formatDateOnly(
											new Date(
												subscriptionData.subscription.currentPeriodEnd,
											).getTime(),
										),
									})}
								</p>
								<p className="mt-0.5 text-base-content/70">
									{t("platformPlan.partnerRedeem.activeBanner.description")}
								</p>
							</div>
						</div>
					</div>
				) : null}

				{/* Plan Selection Header */}
				<p className="text-base-content/70">
					{t("common.choosePlanDescription")}
				</p>

				<PlatformPlanContent
					orgId={orgId}
					onFreePlanSuccess={handleFreePlanSuccess}
					onTrialSuccess={handleTrialSuccess}
				/>
			</div>
		</div>
	);
}
